import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { accountApi } from "../../services/api";
import { formatNumber, formatDateWithTime } from "../../utils/formatters";
import { MODEL_FAMILIES } from "../../utils/constants";
import LoadingSpinner from "../LoadingSpinner";

const FAMILY_FILTERS = [
  { key: "", label: "All" },
  ...MODEL_FAMILIES.map(({ key, label }) => ({ key, label })),
];

// SYNC REQUIRED: labels must cover all types in credit_transactions.type.
// "monthly_allocation", "purchase", "admin_adjustment", "refund" come from
//   VALID_CREDIT_TYPES in creditService.js (enforced by addCredits validation).
// "usage" is written directly by deductCredit — it is NOT in VALID_CREDIT_TYPES.
// If a new type is added to either place, add a label here.
const TYPE_LABELS = {
  monthly_allocation: "Monthly Allocation",
  purchase: "Purchase",
  usage: "Usage",
  admin_adjustment: "Admin Adjustment",
  refund: "Refund",
};

const PAGE_SIZE = 20;

// #3: Extracted from nested ternary in the table row for readability.
function amountClass(amount) {
  if (amount == null) return "";
  return amount >= 0 ? "credit-amount-positive" : "credit-amount-negative";
}

function formatAmount(amount) {
  if (amount == null) return "\u2014";
  return `${amount >= 0 ? "+" : ""}${formatNumber(amount)}`;
}

/**
 * Credit transaction history with family filter and pagination.
 * Fetches from GET /api/account/credit-history.
 */
const CreditHistory = ({ toast }) => {
  const [family, setFamily] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);

  const fetchHistory = useCallback(
    async (familyFilter, pageNum) => {
      // Abort previous in-flight request
      if (controllerRef.current) controllerRef.current.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        const params = {
          page: pageNum,
          limit: PAGE_SIZE,
          signal: controller.signal,
        };
        if (familyFilter) params.family = familyFilter;
        const data = await accountApi.getCreditHistory(params);
        setTransactions(data.transactions || []);
        setTotal(data.total || 0);
      } catch (err) {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
        const msg = "Failed to load credit history.";
        setError(msg);
        if (toast) toast.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    fetchHistory(family, page);
    return () => {
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, [family, page, fetchHistory]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleFamilyChange = (newFamily) => {
    setFamily(newFamily);
    setPage(1);
  };

  return (
    <section className="settings-section">
      <h2 className="settings-section-title">Credit History</h2>
      <div className="settings-card">
        {/* Family filter */}
        <div className="credit-history-filters">
          {FAMILY_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              className={`credit-history-filter-btn ${family === key ? "credit-history-filter-btn--active" : ""}`}
              aria-pressed={family === key}
              onClick={() => handleFamilyChange(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner size="small" text="Loading history..." />
        ) : error ? (
          <div className="settings-empty">
            <p>{error}</p>
            <button
              className="billing-manage-btn"
              onClick={() => fetchHistory(family, page)}
              style={{ marginTop: "0.5rem" }}
            >
              Retry
            </button>
          </div>
        ) : transactions.length === 0 ? (
          <p className="settings-empty">
            {family
              ? `No transactions found for ${FAMILY_FILTERS.find((f) => f.key === family)?.label ?? family}.`
              : "No transactions found."}
          </p>
        ) : (
          <>
            <table className="settings-table credit-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Family</th>
                  <th>Amount</th>
                  <th>Balance After</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>{formatDateWithTime(tx.created_at)}</td>
                    <td>{TYPE_LABELS[tx.type] || tx.type}</td>
                    <td className="credit-history-family">{tx.model_family}</td>
                    <td className={amountClass(tx.amount)}>
                      {formatAmount(tx.amount)}
                    </td>
                    <td>
                      {tx.balance_after == null
                        ? "\u2014"
                        : formatNumber(tx.balance_after)}
                    </td>
                    <td
                      className="credit-history-desc"
                      title={tx.description || undefined}
                    >
                      {tx.description || "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="credit-history-pagination">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <span className="credit-history-page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

CreditHistory.propTypes = {
  toast: PropTypes.shape({
    success: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
};

export default CreditHistory;
